import type { FieldErrors, Resolver } from 'react-hook-form';
import type { TAutocompleteOption } from '~/components/AutocompleteField/types.ts';
import { errorMessages } from '~/constants/errorMessages.ts';
import {
  GuaranteesContractName,
  GuaranteesMainDealConditionsFieldName,
  GuaranteesMainDealConditionsStatus,
  GuaranteesRiskApprovalStatus,
  type TGuaranteesMainDealConditions,
  type TGuaranteesMainDealConditionsValues,
  type TPatchGuaranteesMainDealConditionsPayload,
} from '~/store/guarantees/types.ts';
import {
  MAIN_DEAL_CONDITIONS_BUSINESS_CATEGORY_OPTIONS,
  MAIN_DEAL_CONDITIONS_CONTRACT_NAME_OPTIONS,
  MAIN_DEAL_CONDITIONS_REQUIRED_FIELDS,
  MAIN_DEAL_CONDITIONS_RISK_APPROVAL_STATUS_OPTIONS,
  MAIN_DEAL_CONDITIONS_STATUS_OPTIONS,
} from './constants.ts';

const getStringValue = (value: unknown): string => String(value ?? '').trim();

const getAutocompleteOptionValue = (value: unknown): string => {
  if (value && typeof value === 'object' && 'value' in value) {
    return getStringValue(value.value);
  }

  return '';
};

const findOption = <TOption extends TAutocompleteOption>(
  options: readonly TOption[],
  value: unknown
): TOption | null => {
  const normalizedValue = getStringValue(value);

  return options.find((option) => option.value === normalizedValue) ?? null;
};

const toResolverErrors = <TValues extends Record<string, unknown>>(
  errors: Partial<Record<keyof TValues, string>>
): FieldErrors<TValues> =>
  Object.entries(errors).reduce<FieldErrors<TValues>>((accumulator, [name, message]) => {
    if (!message) {
      return accumulator;
    }

    return {
      ...accumulator,
      [name]: {
        type: 'manual',
        message,
      },
    };
  }, {});

const getBusinessCategoryLabel = (
  conditions?: TGuaranteesMainDealConditions | null
): string => {
  const option = findOption(
    MAIN_DEAL_CONDITIONS_BUSINESS_CATEGORY_OPTIONS,
    conditions?.businessCategory
  );

  return option?.label ?? getStringValue(conditions?.businessCategory);
};

export const getManagerMainDealConditionsInitialValues = (
  conditions?: TGuaranteesMainDealConditions | null
): TGuaranteesMainDealConditionsValues => ({
  [GuaranteesMainDealConditionsFieldName.BusinessCategory]:
    getBusinessCategoryLabel(conditions),
  [GuaranteesMainDealConditionsFieldName.RiskApprovalStatus]: findOption(
    MAIN_DEAL_CONDITIONS_RISK_APPROVAL_STATUS_OPTIONS,
    conditions?.riskApprovalStatus
  ),
  [GuaranteesMainDealConditionsFieldName.ContractName]: findOption(
    MAIN_DEAL_CONDITIONS_CONTRACT_NAME_OPTIONS,
    conditions?.contractName
  ),
  [GuaranteesMainDealConditionsFieldName.MaxGuaranteeTermDays]: getStringValue(
    conditions?.maxGuaranteeTermDays
  ),
  [GuaranteesMainDealConditionsFieldName.MainDealConditionsStatus]: findOption(
    MAIN_DEAL_CONDITIONS_STATUS_OPTIONS,
    conditions?.mainDealConditionsStatus
  ),
  [GuaranteesMainDealConditionsFieldName.NormativeCoverageRatio]: getStringValue(
    conditions?.normativeCoverageRatio
  ),
});

export const validateManagerMainDealConditions = (
  values: TGuaranteesMainDealConditionsValues
): Partial<Record<keyof TGuaranteesMainDealConditionsValues, string>> => {
  const errors: Partial<Record<keyof TGuaranteesMainDealConditionsValues, string>> = {};

  MAIN_DEAL_CONDITIONS_REQUIRED_FIELDS.forEach((fieldName) => {
    const value = values[fieldName];
    const isSelectField =
      fieldName === GuaranteesMainDealConditionsFieldName.RiskApprovalStatus ||
      fieldName === GuaranteesMainDealConditionsFieldName.ContractName ||
      fieldName === GuaranteesMainDealConditionsFieldName.MainDealConditionsStatus;
    const normalizedValue = isSelectField
      ? getAutocompleteOptionValue(value)
      : getStringValue(value);

    if (!normalizedValue) {
      errors[fieldName] = isSelectField
        ? errorMessages.OPTION_REQUIRED
        : errorMessages.FIELD_REQUIRED;
    }
  });

  const maxGuaranteeTermDays = getStringValue(values.maxGuaranteeTermDays);
  const parsedMaxGuaranteeTermDays = Number(maxGuaranteeTermDays);

  if (
    maxGuaranteeTermDays &&
    (!/^\d+$/.test(maxGuaranteeTermDays) ||
      !Number.isInteger(parsedMaxGuaranteeTermDays) ||
      parsedMaxGuaranteeTermDays <= 0)
  ) {
    errors.maxGuaranteeTermDays = errorMessages.INVALID_INPUT_DATA;
  }

  return errors;
};

export const managerMainDealConditionsResolver: Resolver<
  TGuaranteesMainDealConditionsValues
> = async (values) => {
  const errors = validateManagerMainDealConditions(values);

  return {
    values: Object.keys(errors).length ? {} : values,
    errors: toResolverErrors<TGuaranteesMainDealConditionsValues>(errors),
  };
};

export const buildManagerMainDealConditionsPayload = (
  values: TGuaranteesMainDealConditionsValues
): TPatchGuaranteesMainDealConditionsPayload => ({
  riskApprovalStatus: values.riskApprovalStatus?.value as GuaranteesRiskApprovalStatus,
  contractName: values.contractName?.value as GuaranteesContractName,
  maxGuaranteeTermDays: Number(values.maxGuaranteeTermDays),
  mainDealConditionsStatus:
    values.mainDealConditionsStatus?.value as GuaranteesMainDealConditionsStatus,
  normativeCoverageRatio: values.normativeCoverageRatio.trim(),
});

export const areManagerMainDealConditionsValid = (
  conditions?: TGuaranteesMainDealConditions | null
): boolean =>
  Object.keys(
    validateManagerMainDealConditions(getManagerMainDealConditionsInitialValues(conditions))
  ).length === 0;
